/**
 * VG-27 — the FOUNDER'S LIST: the early-partner programme, as a field on the sales company and
 * its applied terms on the deal. Canon: vera-inc `gtm/design-partner-terms.md`; filled example
 * `gtm/sales/ofy-founders-list-terms.md`.
 *
 * ⚠️ INTERNAL ONLY. Membership names which brands Spencer has selected, so it reveals the whole
 * list from any one row. Naming a client publicly needs that client's written OK (standing rule),
 * and the terms sheet is what secures it — so the field lives behind GTM → Sales
 * (`internal_gtm_view` + `internal_sales_manage`) and no brand- or consumer-facing payload may
 * carry it. The glossary entries are `audience: internal` for the same reason.
 *
 * ── Why membership is on the COMPANY and the terms are on the DEAL ──────────────────────────
 * Membership is a property of the BRAND: it survives renewals, an expansion and a second deal, and
 * the canon's badge is explicitly permanent ("a permanent place on the Vera Founder's List"). On a
 * deal it would be duplicated, and a company with two deals could hold two different answers to
 * "is this brand on the list". So it sits on `sales_accounts`.
 *
 * The TERMS attach to one agreement with one end date — a waived base across a six-month term and
 * a bank released against named deliverables — so they sit on `sales_deals`. A second deal at the
 * same brand has its own term, or none.
 *
 * It is on the SALES company (`sales_accounts`), not the platform tenant (`companies`), because
 * `invited` exists before anything is signed: a brand offered the terms has no tenant row yet, and
 * a field that cannot hold the first state of its own lifecycle is the wrong field.
 *
 * ── Why an enum and not a boolean ───────────────────────────────────────────────────────────
 * vera-inc `gtm/client_roi_clustering_future.md` — design deal fields as enums/FKs now so they
 * group later. The states are not binary, and two of them (`invited`, `alumni`) are precisely the
 * ones a count of "how full is the list" must EXCLUDE: an invitation is not a seat, and an alumnus
 * has handed theirs back while keeping the badge. `occupiesFoundersListSeat` is that distinction,
 * in one place, because it is the programme's cap.
 *
 * ── The cap this field exists to make visible ───────────────────────────────────────────────
 * The canon (§5.1, revised) removed the arbitrary five-slot limit: the list caps itself, because
 * Spencer personally manages every account on it. That is a stronger cap but an INVISIBLE one —
 * nothing in the CRM showed how full the list was, which is why this field was asked for. The
 * pill on GTM → Sales → Companies and the filter facet over this column ARE the instrument.
 *
 * `founding_member` stays a state of its own, not a flag beside `member`, because scarcity by time
 * is real while it lasts ("one of the first five on the list") and it is what the term sheet
 * prints. `member` is the programme after that; the canon is explicit that the list outlives
 * "founding".
 */

export const FOUNDERS_LIST_STATUSES = ['invited', 'founding_member', 'member', 'alumni'] as const;
export type FoundersListStatus = (typeof FOUNDERS_LIST_STATUSES)[number];

export const isFoundersListStatus = (v: unknown): v is FoundersListStatus =>
  typeof v === 'string' && (FOUNDERS_LIST_STATUSES as readonly string[]).includes(v);

export interface FoundersListStatusMeta {
  /** Short enough for one line in a pill and a matrix column (working-conventions §25). */
  label: string;
  /** Who this is, for the picker's second line and the drawer. */
  note: string;
}

export const FOUNDERS_LIST_STATUS_META: Readonly<Record<FoundersListStatus, FoundersListStatusMeta>> = {
  invited: {label: 'Invited', note: 'Offered the terms; nothing signed yet'},
  founding_member: {label: 'Founding member', note: 'One of the first five on the list'},
  member: {label: 'Member', note: 'On the list, joined after the founding five'},
  alumni: {label: 'Alumni', note: 'Term ended; still named, and permissions already granted survive'},
};

/** "Founding member"; an empty string for no value, so a caller picks its own empty state. */
export const foundersListStatusLabel = (s: FoundersListStatus | null | undefined): string => (s ? FOUNDERS_LIST_STATUS_META[s].label : '');

/**
 * The states that occupy a seat — a brand Spencer is managing himself RIGHT NOW. `invited` has not
 * accepted and `alumni` has finished, so neither costs calendar. This is the count that answers
 * "how full is the list", and it is the only definition of it.
 */
export const FOUNDERS_LIST_SEAT_STATUSES: readonly FoundersListStatus[] = ['founding_member', 'member'];

export const occupiesFoundersListSeat = (s: FoundersListStatus | null | undefined): boolean =>
  !!s && FOUNDERS_LIST_SEAT_STATUSES.includes(s);

/**
 * The terms as APPLIED to one deal (`sales_deals.founders_*`). All four are independently nullable:
 * a term can be agreed before its bank is sized, and an offer under discussion carries none of them.
 *
 *   waived_base_months  the canon's 6-to-12-month waiver (§1). The base fee, not the rate: the only
 *                       sanctioned rate concession is a negotiated Enterprise contract (pricing §3).
 *   activation_bank     the bank of additional activations (§2). Sized to the brand's first
 *                       production run, never to a round number — a round number invites a
 *                       round-number counter.
 *   bank_released_at    WHEN the bank was released, which is also WHETHER. A timestamp rather than a
 *                       boolean because the release is a milestone against delivered items (§5.2),
 *                       and the date is the thing anyone asks about afterwards.
 *   term_ends_on        the date the waiver expires and the base resumes. The month-7 cliff the
 *                       canon names as the classic churn moment, and the reason this is stored at
 *                       all rather than inferred from the signing date.
 */
export interface FoundersListTerms {
  waived_base_months: number | null;
  activation_bank: number | null;
  /** ISO timestamp, or null while the bank is agreed but not yet released. */
  bank_released_at: string | null;
  /** ISO day (YYYY-MM-DD). */
  term_ends_on: string | null;
}

export const EMPTY_FOUNDERS_LIST_TERMS: FoundersListTerms = {
  waived_base_months: null,
  activation_bank: null,
  bank_released_at: null,
  term_ends_on: null,
};

/** True when the deal carries any Founder's List term at all. Nothing set = ordinary deal. */
export const hasFoundersListTerms = (t: FoundersListTerms | null | undefined): boolean =>
  !!t && (t.waived_base_months !== null || t.activation_bank !== null || t.bank_released_at !== null || t.term_ends_on !== null);

/** The RANGES the CHECK constraints enforce, so the API's parse and the migration cannot disagree. */
export const FOUNDERS_LIST_LIMITS = {
  /** The canon offers 6 to 12; 36 is a generous ceiling that still catches a typed year count. */
  maxWaivedBaseMonths: 36,
  /** A bank is activations, not money. 1,000,000 is a sanity bound, not a policy. */
  maxActivationBank: 1_000_000,
} as const;

/**
 * Days until the waived base ends: 0 on the day, negative once it has passed, null without a term
 * end date. Whole days from the ISO day in UTC, so it does not drift with the reader's clock.
 */
export const foundersTermDaysLeft = (termEndsOn: string | null | undefined, now: Date = new Date()): number | null => {
  if (!termEndsOn) return null;
  const end = Date.parse(`${termEndsOn}T00:00:00Z`);
  if (!Number.isFinite(end)) return null;
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.round((end - today) / 86_400_000);
};

/**
 * The handover window: the term ends within 60 days, or has already. What makes the month-7
 * handover visible BEFORE it arrives (the base resuming, and the plan moving off Business to
 * whatever fits), which is the dependency the ticket named. 60 days because the conversation it
 * prompts — which plan, what volume — takes a cycle, not a week.
 */
export const FOUNDERS_TERM_SOON_DAYS = 60;

export const foundersTermIsEnding = (termEndsOn: string | null | undefined, now: Date = new Date()): boolean => {
  const days = foundersTermDaysLeft(termEndsOn, now);
  return days !== null && days <= FOUNDERS_TERM_SOON_DAYS;
};
